import { CalendarDay, MonthYear, TargetMonthCalendarDay } from './types';
import { DateTime } from 'luxon';

// check sept 2018

const getNextMonthYear = (monthYear: MonthYear): MonthYear => {
    const { month, year } = monthYear;
    return {
        month: month === 12 ? 1 : month + 1,
        year: month === 12 ? year + 1 : year,
    };
};

const getPreviousMonthYear = (monthYear: MonthYear): MonthYear => {
    const { month, year } = monthYear;
    return {
        month: month === 1 ? 12 : month - 1,
        year: month === 1 ? year - 1 : year,
    };
};

const getCalendarDaysFromMonthYear = (monthYear: MonthYear): CalendarDay[] => {
    const { month, year } = monthYear;
    const firstDayOfMonth = DateTime.fromObject({ year, month, day: 1 });
    const lastDayOfMonth = firstDayOfMonth.endOf('month');
    const daysInMonth = lastDayOfMonth.day;
    const firstDayOfWeek = firstDayOfMonth.weekday;
    const lastDayOfWeek = lastDayOfMonth.weekday;
    const previousMonthDaysPadding = firstDayOfWeek === 7 ? 0 : firstDayOfWeek;
    const nextMonthDaysPadding = lastDayOfWeek === 7 ? 6 : 6 - lastDayOfWeek;
    const previousMonthDaysInMonth = firstDayOfMonth.minus({ days: 1 }).daysInMonth!;
    const previousMonthYear: MonthYear = getPreviousMonthYear(monthYear);
    const nextMonthYear: MonthYear = getNextMonthYear(monthYear);

    const calendarDays: CalendarDay[] = [];
    const getWeekAndWeekDayIndex = () => {
        const countWithDayAdded = calendarDays.length + 1;
        const weekIndex = Math.floor(countWithDayAdded / 7);
        const weekDayIndex = (countWithDayAdded % 7) - 1;

        return { weekIndex, weekDayIndex };
    };

    for (let i = 0; i < previousMonthDaysPadding; i++) {
        calendarDays.push({
            type: 'previous-month-calendar-day',
            monthYear: previousMonthYear,
            ...getWeekAndWeekDayIndex(),
            dayOfMonthIndex: previousMonthDaysInMonth - previousMonthDaysPadding + i,
            isoDate: DateTime.fromObject({
                year: previousMonthYear.year,
                month: previousMonthYear.month,
                day: previousMonthDaysInMonth - previousMonthDaysPadding + i + 1,
            }).toISODate()!,
        });
    }

    for (let i = 0; i < daysInMonth; i++) {
        calendarDays.push({
            type: 'target-month-calendar-day',
            ...getWeekAndWeekDayIndex(),
            dayOfMonthIndex: i,
            monthYear,
            isoDate: DateTime.fromObject({
                year: monthYear.year,
                month: monthYear.month,
                day: i + 1,
            }).toISODate()!,
        });
    }

    for (let i = 0; i < nextMonthDaysPadding; i++) {
        calendarDays.push({
            type: 'next-month-calendar-day',
            monthYear: nextMonthYear,
            ...getWeekAndWeekDayIndex(),
            dayOfMonthIndex: i,
            isoDate: DateTime.fromObject({
                year: nextMonthYear.year,
                month: nextMonthYear.month,
                day: i + 1,
            }).toISODate()!,
        });
    }

    return calendarDays;
};

export class CalendarMonthHelper {
    static getFromMonthYear(monthYear: MonthYear): CalendarMonthHelper {
        return new CalendarMonthHelper(getCalendarDaysFromMonthYear(monthYear));
    }

    private _calendarDays: CalendarDay[];
    private _firstDayOfMonth: TargetMonthCalendarDay;

    constructor(calendarDays: CalendarDay[]) {
        this._calendarDays = calendarDays;
        this._firstDayOfMonth = calendarDays.find(
            (calendarDay) => calendarDay.type === 'target-month-calendar-day',
        ) as TargetMonthCalendarDay;
    }

    public getWeeks(): CalendarDay[][] {
        const weeks: CalendarDay[][] = [];
        let week: CalendarDay[] = [];

        this._calendarDays.forEach((calendarDay) => {
            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }

            week.push(calendarDay);
        });

        if (week.length > 0) {
            weeks.push(week);
        }

        return weeks;
    }

    public get nextMonthYear(): MonthYear {
        return getNextMonthYear(this._firstDayOfMonth.monthYear);
    }

    public get currentYear(): number {
        return this._firstDayOfMonth.monthYear.year;
    }

    public get previousMonthYear(): MonthYear {
        return getPreviousMonthYear(this._firstDayOfMonth.monthYear);
    }

    public get monthYearDisplay(): string {
        return DateTime.fromObject({
            year: this._firstDayOfMonth.monthYear.year,
            month: this._firstDayOfMonth.monthYear.month,
            day: 1,
        }).toFormat('LLLL yyyy');
    }
}
